export enum FilterAvailability {
  ORGANIZATION_AND_SUPPLIERS = "ORGANIZATION_AND_SUPPLIERS",
  ORGANIZATION = "ORGANIZATION",
  PRIVATE = "PRIVATE",
  SUPPLIER_GLOBAL = "SUPPLIER_GLOBAL",
}

export const assetTableKeys = [
  "ASSETS",
  "COMPONENTS_GENERAL",
  "COMPONENTS_PUMP_AND_MECHANICAL",
  "COMPONENTS_ELECTRICS",
  "COMPONENTS_CONSTRUCTION",
  "EXTERNAL_KEYS",
  "MEASUREMENTS",
  "SMART_MEASUREMENTS",
  "RELATIONS",
  "ASSET_INSPECTIONS",
  "ASSET_ACTIONS",
  "ASSET_MALFUNCTIONS",
  "ASSET_BUDGETS",
] as const
export const ticketTableKeys = ["TICKETS", "TICKETS_COSTS"] as const
export const analysisTableKeys = ["RISK", "INSPECTION_ANALYSIS", "DATA_QUALITY"] as const
export const financeTableKeys = ["BUDGETS", "CONTRACTS", "COST_SPECIFICATION", "SUPPLIER_PRICELISTS"] as const
export const sharedTableKeys = ["SHARED_ASSETS", "SHARED_TICKETS"] as const
export const adminTableKeys = [
  "ADMIN_COMPONENT_TYPES",
  "ADMIN_TEMPLATES",
  "ADMIN_TICKET_TEMPLATES",
  "ADMIN_LOOKUP_TYPES",
  "ADMIN_LOOKUP_VALUES",
  "ADMIN_ORGANIZATIONS",
  "ADMIN_SECURITY_LOGS",
  "ADMIN_USERS",
  "ADMIN_API_KEYS",
  "ADMIN_FILES",
  "ADMIN_EXTERNAL_KEY_APPLICATIONS",
  "REQUEST_LOG",
  "ADMIN_MONGO_INDEX_STATS",
  "ADMIN_IMPORT_ASSETS",
  "ADMIN_IMPORT_ASSET_COMPONENTS",
] as const
export const mutationLogTableKeys = ["MUTATION_LOG"] as const
export const organizationTableKeys = ["ORGANIZATIONS"] as const

export const tableKeys = [
  ...assetTableKeys,
  ...ticketTableKeys,
  ...analysisTableKeys,
  ...financeTableKeys,
  ...sharedTableKeys,
  ...adminTableKeys,
  ...mutationLogTableKeys,
  ...organizationTableKeys,
]

export type TableKey = (typeof tableKeys)[number]
export interface FilterPreset<Columns extends string> {
  _id: string
  description: string
  tableKey: TableKey
  organization: string
  createdBy: { _id: string; description: string }
  createdByOrganization: string
  availability: FilterAvailability
  sortBy?: Array<SortItem>

  filters: Record<string, FilterValue>
  columns: Array<Columns>
}

export interface MinimalFilterHeader {
  key: string
  children?: Array<MinimalFilterHeader>
}

export type FilterPresetPayload<Columns extends string> = Pick<
  FilterPreset<Columns>,
  "description" | "filters" | "columns" | "sortBy" | "tableKey" | "availability"
>

export const useFilterPresetStore = defineStore("filter-preset", () => {
  const getAvailableFilterPresetsPerTableKey = async <Columns extends string>(
    organization: string,
    tableKey: string,
  ): Promise<Array<FilterPreset<Columns>>> => {
    const { data } = await useSamApi<Array<FilterPreset<Columns>>>(`/filter-presets/organization/${organization}/${tableKey}`, {
      method: "GET",
    })

    return data
  }

  const getAvailableFilterPresets = async <Columns extends string>(organization: string): Promise<Array<FilterPreset<Columns>>> => {
    const { data } = await useSamApi<Array<FilterPreset<Columns>>>(`/filter-presets/organization/${organization}`, { method: "GET" })

    return data
  }

  const createFilterPreset = async <Columns extends string>(
    filterPresetPayload: FilterPresetPayload<Columns>,
  ): Promise<FilterPreset<Columns>> => {
    const { data } = await useSamApi<FilterPreset<Columns>>(`/filter-presets`, {
      method: "POST",
      body: JSON.stringify({ filterPreset: filterPresetPayload }),
    })

    return data
  }

  const updateFilterPresetDescription = async <Columns extends string>(
    filterPresetId: string,
    filterPresetPayload: FilterPresetPayload<Columns>,
  ): Promise<FilterPreset<Columns>> => {
    const { data } = await useSamApi<FilterPreset<Columns>>(`/filter-presets/${filterPresetId}`, {
      method: "PUT",
      body: JSON.stringify({ filterPresetPayload }),
    })

    return data
  }

  const deleteFilterPreset = async (filterPresetId: string): Promise<void> => {
    await useSamApi(`/filter-presets/${filterPresetId}`, {
      method: "DELETE",
    })
  }

  return {
    getAvailableFilterPresetsPerTableKey,
    getAvailableFilterPresets,
    createFilterPreset,
    updateFilterPresetDescription,
    deleteFilterPreset,
  }
})
